/**
 * Mock data for the competitive / marketing surfaces (rankings, tournaments, lobby rails,
 * home live-peek). Single source of mock data so each export is trivially swappable for a
 * real endpoint as the backend lands. See
 * `.docs/plans/euchre-multiplayer-expansion/08-marketing-and-product-pages.md` §2.
 *
 * NOTE: this is mock *data*, not stubbed production logic — it is clearly named and isolated
 * here, never inlined into components.
 */
import type { RankTier } from '@/lib/ranks'
import { RANK_TIERS, tierRangeLabel } from '@/lib/ranks'

// ── Ranked ladder (rankings page header) ─────────────────────────────────────
export interface LadderTierRow {
  tier: RankTier
  range: string
}
export const SAMPLE_LADDER: LadderTierRow[] = RANK_TIERS.map((t) => ({
  tier: t.name,
  range: tierRangeLabel(t),
}))

// ── Tournaments ──────────────────────────────────────────────────────────────
export type TournamentStatus = 'Live' | 'Registering' | 'Starts soon' | 'Upcoming'
export type TournamentAccessTag = 'FREE ENTRY' | 'PLUS' | 'RANKED' | 'OPEN'
export interface TournamentRow {
  name: string
  when: string
  players: string
  format: string
  /** Sponsor-funded prize description (no cash pot / entry fee). */
  prize: string
  status: TournamentStatus
  tier?: RankTier
  accessTag: TournamentAccessTag
}
export const SAMPLE_TOURNAMENTS: TournamentRow[] = [
  { name: 'Sponsor Cup', when: 'Sat · 7:00 PM ET', players: '64 players', format: 'Single elim · to 10', prize: 'Sponsor prize pool', status: 'Registering', tier: 'Right Bower', accessTag: 'PLUS' },
  { name: 'Mitten Masters', when: 'Sun · 3:00 PM ET', players: '32 players', format: 'Double elim', prize: 'Partner prizes + points', status: 'Registering', tier: 'Ace', accessTag: 'PLUS' },
  { name: 'Friday Ladder Night', when: 'Fri · 8:00 PM ET', players: '128 players', format: 'Swiss · 6 rounds', prize: 'Ranking points', status: 'Upcoming', tier: 'King', accessTag: 'RANKED' },
  { name: 'Lakeshore Open', when: 'Next Sat', players: '64 players', format: 'Single elim', prize: 'Sponsor prizes', status: 'Upcoming', accessTag: 'OPEN' },
  { name: 'Weeknight Qualifier', when: 'Live now', players: '16 players', format: 'Single elim', prize: 'Seeds the Sponsor Cup', status: 'Live', tier: 'Queen', accessTag: 'PLUS' },
  { name: 'Newcomers Cup', when: 'Wed · 7:30 PM ET', players: '32 players', format: 'Single elim · to 10', prize: 'Prizes + first rating', status: 'Registering', accessTag: 'FREE ENTRY' },
]

// ── Live activity feed (lobby right rail) ────────────────────────────────────
export interface ActivityRow {
  avatarSeed: string
  who: string
  did: string
  when: string
}

// ── Friends rail ─────────────────────────────────────────────────────────────
export type FriendStatus = 'In lobby' | 'At a table'
export interface FriendRow {
  name: string
  avatarSeed: string
  status: FriendStatus
}

// ── Lobby tables (public + partner) ──────────────────────────────────────────
export type TableKind = 'PUBLIC' | 'RANKED' | 'PRIVATE'
export interface LobbyTableRow {
  name: string
  host: string
  seats: number
  kind: TableKind
  tier?: RankTier
}

// ── Entitlement (free private-game meter) — until `07` backend lands ──────────
export interface EntitlementSnapshot {
  tier: 'free' | 'plus' | 'club'
  privateGamesUsed: number
  privateGamesLimit: number
}
export const SAMPLE_ENTITLEMENT: EntitlementSnapshot = {
  tier: 'free',
  privateGamesUsed: 1,
  privateGamesLimit: 3,
}

// Avatar cluster seeds for the home HeroTrust row.
export const SAMPLE_HERO_TRUST_SEEDS = ['classicHost', 'brightBidder', 'retroShades', 'cozyStrat']
